import Agent from "core/services/api";
import Configuration from "Configuration";
import { Segment, Channel, Result } from "core/models";

type OrderFields = [string, boolean, string?, boolean?, string?, boolean?];

/**
 * Lists all channels for a given project
 *
 * @param accessToken Authorization token
 * @param projectID The ID of the project to list channels from
 * @param currentPage The page index to list
 * @param pageSize Size of the page to calculate the range for using the page index
 */
export async function listChannels(
    accessToken: string,
    projectID = 0,
    orderFields: OrderFields = ["id", true],
    maxCount = 1000,
): Promise<Result<Channel[], string>> {
    const request = {
        projectId: projectID,
        orderFields,
        maxCount,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Channel[]>(
        "channel/list/v1",
        request,
        headers,
        "site",
    );

    return {
        value: response.resultObject,
        error: undefined,
    };
}

export async function getChannel(
    accessToken: string,
    channelID: number,
): Promise<Result<Channel, string>> {
    const request = { id: channelID };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Channel>(
        "channel/get/v1",
        request,
        headers,
        "site",
    );

    return {
        value: response.resultObject,
        error: undefined,
    };
}

/**
 * Lists given backwards counting from the given timestamp
 *
 * @param accessToken The access token to use for authorization
 * @param channelID Id of the channel to list segments of
 * @param timestamp The timestamp to start counting from
 * @param start The starting number of segments (backwards)
 * @param end The end number of segments
 */
export async function listSegmentsByTimestamp(
    accessToken: string,
    channelID: number,
    timestamp: number,
    start: number,
    end: number,
): Promise<Result<Segment[], string>> {
    const request = {
        channelId: channelID,
        timestamp,
        start,
        end,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Segment[]>(
        "channel/listsegmentsbytime/v1",
        request,
        headers,
        "site",
    );
    return {
        value: response.resultObject,
        error: undefined,
    };
}

/**
 * Lists given segments backwards by start / end ranges
 * This function is not exported as its usage
 * is more difficult than one would like from an API and so is abstracted away
 * by listNextSegments and listPreviousSegments
 *
 * @param accessToken The access token to use for authorization
 * @param channelID Id of the channel to list segments of
 * @param number The segment ID to start counting from
 * @param start The starting number of segments (backwards)
 * @param end The end number of segments
 */
export async function listSegmentsByNumber(
    accessToken: string,
    channelID: number,
    segmentNumber: string,
    start: number,
    end: number,
): Promise<Result<Segment[], string>> {
    const request = {
        channelId: channelID,
        number: segmentNumber,
        start,
        end,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Segment[]>(
        "channel/listsegmentsbynumber/v1",
        request,
        headers,
        "site",
    );
    return {
        value: response.resultObject,
        error: undefined,
    };
}

/**
 *
 *
 * @param accessToken Authorization token from the current session
 * @param channelID The id of the channel
 * @param segmentNumber The timestamp_segment number string from a segment to list from
 * @param requestedCount The amount to attempt to list, in case only few or no segments are available the result count might be lower
 */
export async function listNextSegments(
    accessToken: string,
    channelID: number,
    segmentNumber: string,
    requestedCount: number,
): Promise<Result<Segment[], string>> {
    const response = await listSegmentsByNumber(
        accessToken,
        channelID,
        segmentNumber,
        requestedCount,
        0,
    );

    // No extra logic is needed as the `segmentNumber` segment is not included in this request
    // The list is returned from oldest to newest in the list ( oldest = 0, newest = {count} )
    return response;
}

/*
Gets previous 10 including the one with the number ( at the last index )
{
    "ChannelId": 1,
    "Number": "1585825949_001",
    "Start": 0,
    "End": 10
}
*/

/*
Gets the next 10 excluding the one in 'number' as 'end' is 0
{
    "ChannelId": 1,
    "Number": "1585825949_001",
    "Start": 10,
    "End": 0
}
*/

/**
 *
 * @param accessToken Authorization token from the current session
 * @param channelID The id of the channel
 * @param segmentNumber The timestamp_segmentCount number string from a segment to list from
 * @param requestedCount The amount to attempt to list, in case none are available anymore less or no segments may be returned
 */
export async function listPreviousSegments(
    accessToken: string,
    channelID: number,
    segmentNumber: string,
    requestedCount: number,
): Promise<Result<Segment[], string>> {
    // +1 as this request also returns the segment tagged with the `segmentNumber`
    const actualRequestedCount = requestedCount + 1;
    const response = await listSegmentsByNumber(
        accessToken,
        channelID,
        segmentNumber,
        0,
        actualRequestedCount,
    );

    if (!response || !response.value) {
        return {
            value: undefined,
            error: "Unable to fetch segments",
        };
    }

    // Next filter the duplicate segment we already have
    const filteredSegments = response.value.filter(
        (segment) => segment.number !== segmentNumber,
    );
    return {
        value: filteredSegments,
        error: undefined,
    };
}

/**
 * List the segments by range in backwards order
 * Meaning 0 is the latest segment
 *
 * @param accessToken The API accessToken
 * @param channelID ChannelID from the channel to list segments from
 * @param start Starting number of segments, default = 0
 * @param end End number of segments, default = 200
 */
export async function listSegments(
    accessToken: string,
    channelID: number,
    start = 0,
    end = 200,
): Promise<Result<Segment[], string>> {
    const request = {
        channelId: channelID,
        start,
        end,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Segment[]>(
        "/channel/listsegmentsbylast/v1",
        request,
        headers,
        "site",
    );
    return {
        value: response.resultObject,
        error: undefined,
    };
}

/**
 * Request a livestream (channel)
 *
 * @param accessToken The API accessToken
 * @param projectId ID of the current project
 * @param message The user's message
 */
export async function requestChannel(
    accessToken: string,
    projectId: number,
    message: string,
): Promise<Result<boolean, string>> {
    const BASE_URL = Configuration.get("baseURL");
    const request = {
        projectId,
        message,
        baseUrl: `${BASE_URL}`,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<null>(
        "/channel/requestcreate/v1",
        request,
        headers,
        "site",
    );

    if (response.messages) {
        return {
            value: undefined,
            error: "Error while requesting channel",
        };
    }

    return {
        value: true,
        error: undefined,
    };
}

/**
 * Creates a RTMP stream with channel
 *
 * @param accessToken The API accessToken
 * @param channelName The given channel name
 * @param projectId ID of the current project
 *
 */
export async function createRtmpStream(
    accessToken: string,
    projectId: number,
    channelName: string,
): Promise<Result<Channel, string>> {
    const request = {
        name: channelName,
        projectId,
        createRtmpEndPoint: true,
        hlsUrl: "",
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Channel>(
        "/channel/createwithexstream/v1",
        request,
        headers,
        "site",
    );

    if (response.messages) {
        return {
            value: undefined,
            error: "Error while creating RTMP livestream",
        };
    }

    return {
        value: response.resultObject,
        error: undefined,
    };
}

/**
 * Creates a HLS stream with channel
 *
 * @param accessToken The API accessToken
 * @param channelName The given channel name
 * @param projectId ID of the current project
 * @param hlsUrl The given HLS URL
 *
 */
export async function createHlsStream(
    accessToken: string,
    projectId: number,
    channelName: string,
    hlsUrl: string,
): Promise<Result<Channel, string>> {
    const request = {
        name: channelName,
        projectId,
        createRtmpEndPoint: false,
        hlsUrl,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<Channel>(
        "/channel/createwithexstream/v1",
        request,
        headers,
        "site",
    );

    if (response.messages) {
        return {
            value: undefined,
            error: "Error while creating HLS Stream",
        };
    }

    return {
        value: response.resultObject,
        error: undefined,
    };
}
